import { useState, useEffect } from "react";
import {
  MapContainer,
} from "react-leaflet";
import L from "leaflet";

import Col from "react-bootstrap/Col";
import Row from "react-bootstrap/Row";
import Spinner from "react-bootstrap/Spinner";
import Alert from "react-bootstrap/Alert";
import ToggleButtonGroup from 'react-bootstrap/ToggleButtonGroup'
import ToggleButton from 'react-bootstrap/ToggleButton'
import Button from 'react-bootstrap/Button'

import { useQuery } from "react-query";
import {InfluxDB, flux} from '@influxdata/influxdb-client-browser';
import { useLocalStorage } from "react-use";

import MapGuts from "./MapGuts";
import PushSubscribe from "./PushSubscribe";

const {
  REACT_APP_INFLUXDB_V2_URL,
  REACT_APP_INFLUXDB_V2_ORG,
  REACT_APP_INFLUXDB_V2_TOKEN,
  REACT_APP_INFLUXDB_V2_BUCKET,
  NETLIFY_DEV,
} = process.env;

const home = [45.53132, -122.68091];
const lvcc_west_hall = [36.13427, -115.15732];
const sampleLatLng = (s) => L.latLng(s.latitude, s.longitude);

const influxApi = new InfluxDB({url: REACT_APP_INFLUXDB_V2_URL, token: REACT_APP_INFLUXDB_V2_TOKEN}).getQueryApi(REACT_APP_INFLUXDB_V2_ORG);

const homeBounds = L.latLngBounds([
  L.latLng(45.531990,-122.681375),
  L.latLng(45.530840,-122.680382),
]);

const query = flux`
  from(bucket: "${REACT_APP_INFLUXDB_V2_BUCKET}")
    |> range(start: -24h)
    |> filter(fn: (r) => r["_measurement"] == "location")
    |> drop(columns: ["id"])
`;

function atHome(sample) {
  const { latitude, longitude } = sample;
  return homeBounds.contains(L.latLng(latitude, longitude));
}

const refetchInterval = NETLIFY_DEV ? 1000 * 30 : 1000 * 60 * 15;

function Current(props) {
  const today = new Date().toLocaleDateString("en-US");
  const [selectedIndex, setSelectedIndex] = useState();
  const [displaySamples, setDisplaySamples] = useState([]);
  const [bounds, setBounds] = useState();
  const [view, setView] = useState('points');
  const [follow, setFollow] = useLocalStorage("follow", true);
  const [flyTo, setFlyTo] = useState(null);

  const { isLoading, error, data: samples = [] } = useQuery("current", async () => {
    const results = await influxApi.collectRows(query);
    const points = results.reduce((hsh, line) => {
      const { _field, _time, _value, who = '' } = line;
      const obj = hsh[_time] || {};
      if (_field === "lat") {
        obj.latitude = _value;
      } else if (_field === "lon") {
        obj.longitude = _value;
      } else {
        obj[_field] = _value;
      }
      obj.timestamp = new Date(_time);
      obj.who = who;
      hsh[_time] = obj;
      return hsh;
    }, {});

    const times = Object.keys(points)
    times.sort().reverse();
    const samples = times.map(t => points[t])
    return samples
  }, { refetchInterval });

  useEffect(() => {
    if (today && samples.length > 0) {
      let displaySamples = samples
        .map(s => {
          if (atHome(s)) {
            s.latitude = home[0];
            s.longitude = home[1];
          }
          return s
        })
        .filter(
          (sample) => sample.timestamp.toLocaleDateString("en-US") === today
        )
        .slice(0, 30)

      if (displaySamples.length === 0) {
        displaySamples = samples.slice(0, 1);
      }
      setDisplaySamples(displaySamples);
      const bounds = L.latLngBounds(displaySamples.map((s) => sampleLatLng(s)));
      setBounds(bounds);
    }
  }, [samples, today]);

  if (isLoading) {
    return (
      <div className="text-center justify-content-center p-5 align-items-center">
        <Spinner animation="border" role="status" size="xl" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center justify-content-center p-5 align-items-center">
        <Alert variant="danger">{error.message}</Alert>
      </div>
    );
  }

  if (displaySamples.length === 0) {
    return (
      <div className="text-center justify-content-center p-5 align-items-center">
        <div>No samples to show</div>
      </div>
    );
  }

  const pointList = displaySamples.map((sample, index) => {
    const { timestamp } = sample;
    const timeString = timestamp.toLocaleTimeString("en-US");
    const dateString = timestamp.toLocaleDateString("en-US");
    const nowDateString = new Date().toLocaleDateString("en-US");
    if (nowDateString === dateString) {
      return (
        <li
          key={index}
          onMouseEnter={() => setSelectedIndex(index)}
          onMouseLeave={() => setSelectedIndex(undefined)}
          style={{
            fontWeight: selectedIndex === index ? "bold" : "normal",
          }}
        >
        {atHome(sample) ? '🏠' : null}{timestamp.toLocaleTimeString("en-US")}
        </li>
      );
    } else {
      return (
        <li key={index}>
          {timeString} {dateString}
        </li>
      );
    }
  });

  const viewToggle = (
    <ToggleButtonGroup type="radio" name="view" value={view} onChange={(v) => setView(v)} className="p-1">
      <ToggleButton value="points">Points</ToggleButton>
      <ToggleButton value="heatmap">Heatmap</ToggleButton>
    </ToggleButtonGroup>
  );

  return (
    <>
      {/* Mobile layout */}
      <div className="d-lg-none">
        <Row className="text-center my-2">
          <Col className="d-flex justify-content-center gap-2">
            <Button active={follow} onClick={(v) => setFollow(!follow)}>{follow ? '' : 'Not '}Following</Button>
            <PushSubscribe />
          </Col>
        </Row>
        <Row>
          <Col style={{height: '66vh'}}>
            <MapContainer bounds={bounds} maxZoom={22} scrollWheelZoom style={{height: '100%'}}>
              <MapGuts
                selectedIndex={selectedIndex}
                displaySamples={displaySamples}
                view={view}
                follow={follow}
              />
            </MapContainer>
          </Col>
        </Row>
        <Row>
          <Col>
            <ol>{pointList}</ol>
          </Col>
        </Row>
      </div>

      {/* Desktop layout */}
      <Row className="d-none d-lg-flex">
        <Col md={2}>
          <Button active={follow} block onClick={(v) => setFollow(!follow)}>{follow ? '' : 'Not '}Following</Button>
          <Button block onClick={() => { setFollow(false); setFlyTo(lvcc_west_hall); }}>🏴‍☠️ DEFCON</Button>
          {viewToggle}
          <div className="py-1"><PushSubscribe /></div>
          <ol>{pointList}</ol>
        </Col>
        <Col lg={10}>
          <MapContainer bounds={bounds} maxZoom={22} scrollWheelZoom>
            <MapGuts
              selectedIndex={selectedIndex}
              displaySamples={displaySamples}
              view={view}
              follow={follow}
              flyTo={flyTo}
              onFlyComplete={() => setFlyTo(null)}
            />
          </MapContainer>
        </Col>
      </Row>
      <Row className="d-none d-md-flex">
        <Col md={12}>
        <p>The points are more often and more accurate while moving and outside, and they skew towards open areas when close to a builidng.</p>
        </Col>
      </Row>
    </>
  );
}
export default Current;
